import {ConsoleLogger} from './ConsoleLogger';

const LogLevels = ['debug', 'info', 'warn', 'error'] as const;
export type LogLevel = (typeof LogLevels)[number];
const LogLevelPriorities: Record<LogLevel, number> = LogLevels.reduce(
  (acc, level, index) => {
    acc[level] = index;
    return acc;
  },
  {} as Record<LogLevel, number>,
);

export type LogFunction = (level: LogLevel, message: string, ...optionalParams: unknown[]) => void;
export type LogPayload = {
  level: LogLevel;
  message: string;
  optionalParams?: unknown[];
};
export type LoggerLike = Pick<Console, 'debug' | 'error' | 'info' | 'warn'> & {log: LogFunction};

export class Logger implements LoggerLike {
  private level: LogLevel = 'info';
  private logger: LoggerLike;

  constructor() {
    this.logger = new ConsoleLogger();
  }

  isDebugEnabled(): boolean {
    return this.shouldLog('debug');
  }
  isErrorEnabled(): boolean {
    return this.shouldLog('error');
  }
  isInfoEnabled(): boolean {
    return this.shouldLog('info');
  }
  isWarnEnabled(): boolean {
    return this.shouldLog('warn');
  }

  ifDebugEnabled(withLogger: (logger: LoggerLike) => void): void {
    if (this.isDebugEnabled()) {
      withLogger(this.logger);
    }
  }
  ifErrorEnabled(withLogger: (logger: LoggerLike) => void): void {
    if (this.isErrorEnabled()) {
      withLogger(this.logger);
    }
  }
  ifInfoEnabled(withLogger: (logger: LoggerLike) => void): void {
    if (this.isInfoEnabled()) {
      withLogger(this.logger);
    }
  }
  ifWarnEnabled(withLogger: (logger: LoggerLike) => void): void {
    if (this.isWarnEnabled()) {
      withLogger(this.logger);
    }
  }

  debug(message: string, ...optionalParams: unknown[]): void {
    this.log('debug', message, ...optionalParams);
  }
  error(message: string, ...optionalParams: unknown[]): void {
    this.log('error', message, ...optionalParams);
  }
  info(message: string, ...optionalParams: unknown[]): void {
    this.log('info', message, ...optionalParams);
  }
  warn(message: string, ...optionalParams: unknown[]): void {
    this.log('warn', message, ...optionalParams);
  }

  log(level: LogLevel, message: string, ...optionalParams: unknown[]): void {
    if (this.shouldLog(level)) {
      this.logger[level](message, ...optionalParams);
    }
  }

  setLevel(level: LogLevel): void {
    this.level = level;
  }

  setLogger(logger: LoggerLike): void {
    this.logger = logger;
  }

  private shouldLog(level: LogLevel): boolean {
    return LogLevelPriorities[level] >= LogLevelPriorities[this.level];
  }
}

export const logger: Logger = new Logger();
